// Dependency-free procedural 3D geometry for a stylized Mount Kilimanjaro.
// Everything here is plain math (no three.js / WebGL library) so it has zero
// external dependencies — vertices are projected to the 2D canvas by hand.

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const BASE_RADIUS = 100;
export const MAX_HEIGHT = 58; // stylized vertical scale (not 1:1 with radius)
export const SUMMIT_METERS = 5895; // Uhuru Peak

// Secondary peak (Mawenzi), real proportions: 5,149 m (~87% of Kibo), ~5 km east
// of the summit across the saddle. Distance/radius are scaled into world units.
const MAWENZI_ANGLE = 12; // degrees
const MAWENZI_DIST = 24;
const MAWENZI_HEIGHT = 5149 / SUMMIT_METERS;
const MAWENZI_RADIUS = 12;

// Shira plateau shoulder, real proportions: 4,005 m (~68% of Kibo), a broad
// collapsed-cone plateau on the western side.
const SHIRA_ANGLE = 195;
const SHIRA_DIST = 38;
const SHIRA_HEIGHT = 4005 / SUMMIT_METERS;
const SHIRA_RADIUS = 46;

// A small parasitic cone on the saddle shoulder, like the minor volcanic vents
// found on Kilimanjaro's flanks — adds a bit of secondary silhouette detail.
const PARASITIC_ANGLE = -35;
const PARASITIC_DIST = 30;
const PARASITIC_HEIGHT = 0.18;
const PARASITIC_RADIUS = 9;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Cheap deterministic pseudo-noise from a handful of summed sine waves. */
function ridgeNoise(x: number, z: number): number {
  return (
    Math.sin(x * 0.18 + z * 0.11) * 0.5 +
    Math.sin(x * 0.05 - z * 0.23 + 1.7) * 0.3 +
    Math.sin(x * 0.37 + z * 0.31 + 4.1) * 0.2
  );
}

/** Very fine grain noise for a mottled, satellite-photo-like texture on the flanks. */
export function grainNoise(x: number, z: number): number {
  return (
    Math.sin(x * 2.1 + z * 1.7) * 0.5 +
    Math.sin(x * 3.4 - z * 2.6 + 1.1) * 0.3 +
    Math.sin(x * 5.7 + z * 4.3 + 2.9) * 0.2
  );
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function polarToXZ(r: number, angleDeg: number): { x: number; z: number } {
  const a = toRad(angleDeg);
  return { x: r * Math.cos(a), z: r * Math.sin(a) };
}

const mawenziCenter = polarToXZ(MAWENZI_DIST, MAWENZI_ANGLE);
const shiraCenter = polarToXZ(SHIRA_DIST, SHIRA_ANGLE);
const parasiticCenter = polarToXZ(PARASITIC_DIST, PARASITIC_ANGLE);

/**
 * Radial "flutes" — the dark erosion valleys that run down the flanks toward
 * the crater, like the ridge-and-gully texture visible on real satellite
 * renders of Kilimanjaro. Returns roughly -1..1; strongest at mid-slope.
 */
export function grooveFactor(x: number, z: number, r01: number): number {
  const angle = Math.atan2(z, x);
  const wobble = ridgeNoise(x * 0.22, z * 0.22) * 1.8;
  const wave = Math.sin(angle * 22 + wobble);
  const amp = smoothstep(0.06, 0.28, r01) * (1 - smoothstep(0.75, 1.0, r01));
  return wave * amp;
}

/** Height of the mountain surface at a given (x, z) position. Returns 0..~MAX_HEIGHT. */
export function terrainHeightAt(x: number, z: number): number {
  const r = Math.sqrt(x * x + z * z);
  const r01 = Math.min(1.4, r / BASE_RADIUS);

  // Broad Kibo dome, rounded near the summit, flattening out toward the base.
  const kiboFalloff = Math.pow(Math.max(0, 1 - r01), 1.7);
  let kibo = MAX_HEIGHT * smoothstep(0, 1, kiboFalloff);

  // Slight crater flattening right at the very top.
  if (r01 < 0.05) {
    kibo *= 0.97 + 0.03 * (r01 / 0.05);
  }

  // Mawenzi bump — narrower, jagged secondary peak.
  const dxM = x - mawenziCenter.x;
  const dzM = z - mawenziCenter.z;
  const distM = Math.sqrt(dxM * dxM + dzM * dzM);
  const mawenziGauss = Math.exp(-Math.pow(distM / MAWENZI_RADIUS, 2));
  const jag = 1 + 0.18 * ridgeNoise(x * 1.4, z * 1.4);
  const mawenzi = MAX_HEIGHT * MAWENZI_HEIGHT * mawenziGauss * jag;

  // Shira plateau — broad, low shoulder.
  const dxS = x - shiraCenter.x;
  const dzS = z - shiraCenter.z;
  const distS = Math.sqrt(dxS * dxS + dzS * dzS);
  const shiraGauss = Math.exp(-Math.pow(distS / SHIRA_RADIUS, 2));
  const shira = MAX_HEIGHT * SHIRA_HEIGHT * shiraGauss;

  // Small parasitic cone — a raised rim around a shallow bowl.
  const dxP = x - parasiticCenter.x;
  const dzP = z - parasiticCenter.z;
  const distP = Math.sqrt(dxP * dxP + dzP * dzP);
  const paraRim = Math.exp(-Math.pow((distP - PARASITIC_RADIUS * 0.55) / (PARASITIC_RADIUS * 0.35), 2));
  const paraFill = Math.exp(-Math.pow(distP / (PARASITIC_RADIUS * 0.55), 2));
  const parasitic = MAX_HEIGHT * PARASITIC_HEIGHT * (paraRim - paraFill * 0.35);

  let height = Math.max(kibo, mawenzi, shira, parasitic > 0 ? parasitic + MAX_HEIGHT * 0.01 : 0);

  // Gentle base slope so the mountain doesn't end in a hard cliff at the edge.
  const baseSlope = MAX_HEIGHT * 0.05 * Math.max(0, 1 - r01 * 0.7);
  height = Math.max(height, baseSlope * 0.2);

  // Fine rocky texture, tapering off near the flat base.
  const textureAmp = 0.9 * smoothstep(0, 0.5, r01 < 1 ? 1 - r01 : 0.05);
  height += ridgeNoise(x, z) * textureAmp;

  // Radial erosion valleys carved into the flanks.
  height += grooveFactor(x, z, r01) * MAX_HEIGHT * 0.045;

  return Math.max(0, height);
}

export interface MountainMesh {
  vertices: Vec3[];
  /** Each face is a triangle of vertex indices. */
  faces: [number, number, number][];
  rings: number;
  segments: number;
}

/**
 * Builds a radial grid mesh (center vertex + concentric rings) covering the
 * mountain footprint, with per-vertex height sampled from terrainHeightAt.
 */
export function buildMountainMesh(rings = 32, segments = 60, extent = BASE_RADIUS * 1.25): MountainMesh {
  const vertices: Vec3[] = [{ x: 0, y: terrainHeightAt(0, 0), z: 0 }];
  const faces: [number, number, number][] = [];

  for (let ring = 1; ring <= rings; ring++) {
    const r = (ring / rings) * extent;
    for (let seg = 0; seg < segments; seg++) {
      const angle = (seg / segments) * 360;
      const { x, z } = polarToXZ(r, angle);
      vertices.push({ x, y: terrainHeightAt(x, z), z });
    }
  }

  const ringStart = (ring: number) => 1 + (ring - 1) * segments;

  // Fan from the center vertex to the first ring.
  for (let seg = 0; seg < segments; seg++) {
    const a = ringStart(1) + seg;
    const b = ringStart(1) + ((seg + 1) % segments);
    faces.push([0, a, b]);
  }

  // Quads (as two triangles) between consecutive rings.
  for (let ring = 1; ring < rings; ring++) {
    for (let seg = 0; seg < segments; seg++) {
      const a = ringStart(ring) + seg;
      const b = ringStart(ring) + ((seg + 1) % segments);
      const c = ringStart(ring + 1) + seg;
      const d = ringStart(ring + 1) + ((seg + 1) % segments);
      faces.push([a, b, d]);
      faces.push([a, d, c]);
    }
  }

  return { vertices, faces, rings, segments };
}

// Elevation-band colors sampled directly (via pixel picking) from the "Kilimanjaro
// National Park" elevation-legend map in the project's reference images: <=2210m
// green, 2210-3130m yellow, 3130-4040m orange, 4040-4960m brown, >4960m rock/ice.
const ELEVATION_BANDS: { at: number; color: [number, number, number] }[] = [
  { at: 0, color: [58, 96, 40] }, // forested lower slopes
  { at: 2210 / SUMMIT_METERS, color: [96, 148, 54] }, // moorland green (sampled + brightened)
  { at: 3130 / SUMMIT_METERS, color: [176, 168, 96] }, // heath/tan (sampled)
  { at: 4040 / SUMMIT_METERS, color: [161, 132, 96] }, // alpine desert (sampled)
  { at: 4960 / SUMMIT_METERS, color: [132, 104, 80] }, // scree/brown (sampled)
  { at: 0.9, color: [150, 140, 128] }, // exposed summit rock
  { at: 1.0, color: [168, 160, 148] }, // exposed summit rock (snow/streaks applied separately)
];

/** Elevation band color for a given height fraction (0..1) of MAX_HEIGHT. */
export function terrainColor(heightFrac: number): [number, number, number] {
  const h = clamp01(heightFrac);
  for (let i = 0; i < ELEVATION_BANDS.length - 1; i++) {
    const a = ELEVATION_BANDS[i];
    const b = ELEVATION_BANDS[i + 1];
    if (h >= a.at && h <= b.at) {
      const t = (h - a.at) / (b.at - a.at || 1);
      return [
        a.color[0] + (b.color[0] - a.color[0]) * t,
        a.color[1] + (b.color[1] - a.color[1]) * t,
        a.color[2] + (b.color[2] - a.color[2]) * t,
      ];
    }
  }
  return ELEVATION_BANDS[ELEVATION_BANDS.length - 1].color;
}

const ICE_COLOR: [number, number, number] = [253, 254, 255];
const ROCK_STREAK: [number, number, number] = [96, 88, 80];

/**
 * Blends a bright snow cap into the summit rock, with darker rock streaks
 * bleeding through along the same radial flutes carved into the terrain —
 * matching how Kibo's remaining glaciers look today (streaked, not a solid
 * uniform ice sheet) and the ridge-and-gully look of the reference photo.
 */
export function applySnowAndStreaks(
  rgb: [number, number, number],
  heightFrac: number,
  x: number,
  z: number,
  r01: number
): [number, number, number] {
  if (heightFrac < 0.82) return rgb;
  const snowAmt = clamp01((heightFrac - 0.82) / 0.09);
  let out: [number, number, number] = [
    rgb[0] * (1 - snowAmt) + ICE_COLOR[0] * snowAmt,
    rgb[1] * (1 - snowAmt) + ICE_COLOR[1] * snowAmt,
    rgb[2] * (1 - snowAmt) + ICE_COLOR[2] * snowAmt,
  ];

  if (heightFrac > 0.85) {
    const groove = grooveFactor(x, z, r01);
    const streak = clamp01((-groove - 0.35) / 0.5) * clamp01((heightFrac - 0.85) / 0.12);
    out = [
      out[0] * (1 - streak) + ROCK_STREAK[0] * streak,
      out[1] * (1 - streak) + ROCK_STREAK[1] * streak,
      out[2] * (1 - streak) + ROCK_STREAK[2] * streak,
    ];
  }

  return out;
}

/** A point on the mountain surface, offset slightly above ground for trail rendering. */
export function surfacePoint(r: number, angleDeg: number, liftFrac = 0.01): Vec3 {
  const { x, z } = polarToXZ(r, angleDeg);
  const y = terrainHeightAt(x, z);
  return { x, y: y + MAX_HEIGHT * liftFrac, z };
}
